import {
  ensure,
  isBoolean,
  isInteger,
  isNotEmptyString,
  isUndefinedOr,
  Time,
  Util,
  UUID
} from 'yougile-util'
import {
  DataObjectId,
  isDataObjectId,
  LicenseInfo,
  RegStatRecord,
  RegStatRecordExtra,
  SelfHostedClient,
  StatPayment,
  StatProlongation,
  tariffRenameMap
} from 'yougile-model'

export interface PipelineStatItem {
  manager: string
  total: number
  notSet: number
  notCommercial: number
  failedToCall: number
  takenToWork: number
  touching: number
}

export interface SalesManager {
  login: string
  isAdmin: boolean
  // managers are assigned to new companies every day automatically
  // this flag enables a manager to be assigned by this procedure
  autoAssignable: boolean
}

export const clientsStates = [
  'taken-to-work',
  'failed-to-call',
  'non-commercial',
  'touching',
  ''
] as const
const clientsStatesSet = new Set<string>(clientsStates)
export type ClientsState = (typeof clientsStates)[number]

export const clientsEmailFilters = [
  'all',
  'corporate',
  'not-corporate'
] as const
export type ClientsEmailFilter = (typeof clientsEmailFilters)[number]

export const clientsBlacklistFilters = [
  'all',
  'blacklist',
  'not-blacklist'
] as const
export type ClientsBlacklistFilter = (typeof clientsBlacklistFilters)[number]

export const clientsTypeFilters = ['all', 'new', 'old'] as const
export type ClientsTypeFilter = (typeof clientsTypeFilters)[number]

export const clientsStateFilters = ['all', '', ...clientsStates] as const
export type ClientsStateFilter = (typeof clientsStateFilters)[number]

export interface ClientsFilter {
  date: {
    from: number
    to: number
  }
  email: ClientsEmailFilter
  blacklist: ClientsBlacklistFilter
  type: ClientsTypeFilter
  state: ClientsStateFilter
  managers: string[]
  allUsers: [number, number]
  rank: [number, number]
  sum: [number, number]
}

export interface PaymentsFilter {
  date: {
    from: number
    to: number
  }
  regDate: {
    from: number
    to: number
  }
  payType: PaymentPayTypeFilter
  tariff: PaymentTariffFilter
  type: PaymentTypeFilter
  expiration: {
    from: number
    to: number
  }
  managers: string[]
}

export interface ProlongationFilter {
  date: {
    from: number
    to: number
  }
}

export const isProlongationFilter = (x: unknown): x is ProlongationFilter => {
  if (!x || typeof x !== 'object') {
    return false
  }

  const filter = x as {[key: string]: unknown}

  if (!filter.date || typeof filter.date !== 'object') {
    return false
  }

  const date = filter.date as {[key: string]: unknown}

  return typeof date.from === 'number' && typeof date.to === 'number'
}
export const tariffFilters = [
  'all',
  ...Object.values(tariffRenameMap ?? {})
] as const
export type PaymentTariffFilter = (typeof tariffFilters)[number]

export const paymentPayTypeFilters = ['all', 'bill', 'online'] as const
export type PaymentPayTypeFilter = (typeof paymentPayTypeFilters)[number]

export const paymentTypeFilters = ['cloud', 'box', 'all'] as const
export type PaymentTypeFilter = (typeof paymentTypeFilters)[number]

export type SalesPagination = {offset: number; limit: number}
export const isSalesPagination = (x: unknown): x is SalesPagination => {
  if (!x || typeof x !== 'object') {
    return false
  }
  const pagination = x as SalesPagination
  return (
    typeof pagination.offset === 'number' &&
    typeof pagination.limit === 'number'
  )
}
export type SalesItem =
  | RegStatRecordExtra
  | StatPayment
  | RegStatRecord
  | StatProlongation
  | SelfHostedClient

export const ensureValidClientsFilter: (
  maybeFilter: unknown
) => asserts maybeFilter is ClientsFilter = maybeFilter => {
  if (!maybeFilter) {
    throw new Error('Invalid sales filter')
  }

  const filter = maybeFilter as ClientsFilter

  ensure(
    typeof filter === 'object' && filter !== null,
    'Filter must be a non-null object'
  )

  ensure('date' in filter, "Filter must have a 'date' property")

  ensure(
    !isNaN(filter.date.from) && !isNaN(filter.date.to),
    `Invalid date format ${filter.date}`
  )
  ensure(
    filter.date.from <= filter.date.to,
    `Start date must be before end date ${filter.date}`
  )

  ensure(
    clientsEmailFilters.includes(filter.email),
    `Invalid email type: ${filter.email}`
  )
  ensure(
    clientsTypeFilters.includes(filter.type),
    `Invalid client type: ${filter.type}`
  )
  ensure(
    clientsBlacklistFilters.includes(filter.blacklist),
    `Invalid blacklist: ${filter.blacklist}`
  )

  const isValidState =
    filter.state === 'all' ||
    filter.state === '' ||
    isClientsState(filter.state)
  ensure(isValidState, `Invalid sales state: ${filter.state}`)

  ensure(
    Array.isArray(filter.managers),
    `Managers must be an array ${filter.managers}`
  )
  filter.managers.forEach((manager, index) => {
    ensure(
      typeof manager === 'string',
      `Manager at index ${index} must be a string`
    )
  })

  if (
    Number.isInteger(filter.allUsers[0]) &&
    Number.isInteger(filter.allUsers[1])
  ) {
    ensure(Array.isArray(filter.allUsers), `AllUsers must be an array`)
    ensure(
      filter.allUsers[0] >= 0 && filter.allUsers[1] > 0,
      `AllUsers must be a positive integer ${filter.allUsers}`
    )
    const [minAllUsers, maxAllUsers] = filter.allUsers
    ensure(
      Number.isInteger(minAllUsers) && minAllUsers >= 0,
      `AllUsers min value must be a non-negative integer ${filter.allUsers}`
    )
    ensure(
      Number.isInteger(maxAllUsers) && maxAllUsers >= 0,
      `AllUsers max value must be a non-negative integer ${filter.allUsers}`
    )
    ensure(
      minAllUsers <= maxAllUsers,
      `AllUsers min value must be less than or equal to max value ${filter.allUsers}`
    )
  }

  ensure(Array.isArray(filter.rank), `Rank must be an array ${filter.rank}`)
  ensure(
    Number.isInteger(filter.rank[0]) &&
      Number.isInteger(filter.rank[1]) &&
      filter.rank[0] >= 0 &&
      filter.rank[1] > 0,
    `Rank must be a positive integer ${filter.rank}`
  )
  const [minRank, maxRank] = filter.rank
  ensure(
    Number.isInteger(minRank) && minRank >= 0,
    `Rank min value must be a non-negative integer ${filter.rank}`
  )
  ensure(
    Number.isInteger(maxRank) && maxRank >= 0,
    `Rank max value must be a non-negative integer ${filter.rank}`
  )
  ensure(
    minRank <= maxRank,
    `Rank min value must be less than or equal to max value ${filter.rank}`
  )
}

export const isClientsState = (s: unknown): s is ClientsState => {
  return typeof s === 'string' && clientsStatesSet.has(s)
}

export interface LicenseSalesInfo extends LicenseInfo {
  machineOrDbKey?: string
  licenseKey?: string
  isApproved: boolean
  manager: string
  isDownloaded?: boolean
  id: UUID
}

export interface CompanySalesInfo {
  manager?: string
  comment?: string
  state?: ClientsState
  autoAssignTime?: number
  crmTaskId?: DataObjectId
  favorite?: boolean
}

export interface SalesManagerDataItem {
  passHash: string
  passSalt: string
  isAdmin: boolean
  autoAssignable: boolean
}

export interface SalesCompanyIndex {
  [companyId: DataObjectId]: CompanySalesInfo
}

export interface SalesSession {
  timestamp: number
  key: string
}

export const maxSessionTime = 31 * Time.day

export const salesManagerFromData = (
  login: string,
  managerData: SalesManagerDataItem
): SalesManager => ({
  login,
  autoAssignable: managerData.autoAssignable,
  isAdmin: managerData.isAdmin
})

export const salesManagersDataToList = (data: {
  [login: string]: SalesManagerDataItem
}): SalesManager[] => {
  return Object.keys(data).map(login =>
    salesManagerFromData(login, data[login])
  )
}

/**
 * CRM company is considered to be the same as feedbackCompany (see conf.ts).
 * Here are stored column ids for various sales steps for a manager.
 */
export interface SalesCrm {
  userId?: DataObjectId
  start?: DataObjectId
  crmManagerId?: number
  partnerEmail?: string
}

export interface CrmUser {
  ID: number
  NAME?: string
  LAST_NAME?: string
  EMAIL?: string
}

export interface SalesProfile {
  userId?: DataObjectId
  login?: string
  password?: string
  isAdmin?: boolean
  autoAssignable?: boolean
  start?: DataObjectId
  crmManagerId?: number
}

export interface ProjectTreeItem {
  id: DataObjectId
  title: string
  children?: ProjectTreeItem[]
}

export const isSalesManager = (
  m: unknown
): m is SalesManager & {password?: string | undefined} => {
  const manager = m as SalesManager & {password?: string | undefined}

  return (
    typeof m === 'object' &&
    isNotEmptyString(manager.login) &&
    isUndefinedOr(isNotEmptyString, manager.password) &&
    isBoolean(manager.autoAssignable) &&
    isBoolean(manager.isAdmin)
  )
}

export const isSalesManagerWithPassword = (
  m: unknown
): m is SalesManager & {password: string} => {
  const manager = m as SalesManager & {password?: string}
  return (
    typeof m === 'object' &&
    isNotEmptyString(manager.login) &&
    isNotEmptyString(manager.password) &&
    isBoolean(manager.autoAssignable) &&
    isBoolean(manager.isAdmin)
  )
}

export const isSalesCrm = (c: unknown): c is SalesCrm => {
  return (
    typeof c === 'object' &&
    isUndefinedOr(isDataObjectId, (c as SalesCrm).start) &&
    isUndefinedOr(isDataObjectId, (c as SalesCrm).userId)
  )
}

export const isSalesLicense = (l: unknown): l is LicenseSalesInfo => {
  const license = l as LicenseSalesInfo
  return (
    typeof l === 'object' &&
    Util.isUUID(license.id) &&
    isUndefinedOr(isNotEmptyString, license.tariff) &&
    isNotEmptyString(license.login) &&
    isUndefinedOr(isBoolean, license.isApproved) &&
    isUndefinedOr(isBoolean, license.isDownloaded) &&
    isUndefinedOr(isNotEmptyString, license.machineOrDbKey) &&
    isNotEmptyString(license.manager) &&
    isInteger(license.date) &&
    isInteger(license.expires) &&
    isInteger(license.maxUsers)
  )
}

export const isSalesBoxClient = (b: unknown): b is SelfHostedClient => {
  const boxClient = b as SelfHostedClient
  return (
    typeof boxClient === 'object' &&
    typeof boxClient.status === 'object' &&
    isNotEmptyString(boxClient.id) &&
    isNotEmptyString(boxClient.name) &&
    isNotEmptyString(boxClient.email) &&
    isUndefinedOr(isNotEmptyString, boxClient.phone)
  )
}
